'use client';

import { useRef, type FC } from 'react';
import { CSSTransition } from 'react-transition-group';
import type { INavigation } from '@/services/models/Navigation/INavigation';
import { CSSProperties } from 'react';
import {
  getCSSTransitionString,
  ICSSTransitionArguments,
  CSSTransitionProperties
} from '@/next-utils/css-utils/scss-utils';
import { Block } from './Block';
import S from '../styles.base.module.scss';

const SECTION_TRANSITION_PROPS = {
  transitionMS: 300,
  transitionDelayMS: 0,
  properties: [CSSTransitionProperties.Opacity, CSSTransitionProperties.Top]
};
/**
 * Props for the Section (vertical column that may contain 1 or more blocks)
 * in the Desktop navigation bar dropdown.
 */
interface ISectionProps {
  /** A single section from the navigation content. */
  section: INavigation;

  /** The name of the top-level navigation category (e.g. "Women"). */
  parentCategoryName?: string;

  /** Wether the section is visible (Tied to the Dropdown visibility prop). */
  isVisible: boolean;

  /** Index of the section (used for transition delay). */
  index: number;

  /** Animation length (CSS transition property). */
  transitionArgs?: ICSSTransitionArguments;

  /**
   * An object of CSS property / value pairs. Property names are camelCase.
   * Similar to the HTML `style` attribute, but is fed an object rather than a
   * string, like the `HTMLElement.style` property in the "vanilla" DOM API.
   * @see https://reactjs.org/docs/dom-elements.html#style
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/style
   */
  style?: CSSProperties;
}

/**
 * Renders blocks of links (a Section). Most of the time there is only
 * one block per section but this component can handle an array
 * of blocks on top of each other.
 */
export const Section: FC<ISectionProps> = ({
  section,
  parentCategoryName,
  isVisible,
  index,
  transitionArgs = SECTION_TRANSITION_PROPS,
  style = {}
}) => {
  const nodeRef = useRef(null);
  const styleWithTransition = {
    ...style,
    transition: getCSSTransitionString(transitionArgs)
  };

  return (
    <CSSTransition
      nodeRef={nodeRef}
      in={isVisible}
      timeout={0}
      classNames={{
        enterDone: S.sectionEntered,
        exit: S.sectionEntered,
        exitActive: S.sectionExiting,
        exitDone: S.sectionHidden
      }}
    >
      <li
        ref={nodeRef}
        role="menuitem"
        className={S.section}
        style={styleWithTransition}
      >
        <Block
          key={section.id}
          block={section}
          parentCategoryName={parentCategoryName}
        />
      </li>
    </CSSTransition>
  );
};
